// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import MiniProductCarouselProduct, {
    MiniProductCarouselProductStyles,
} from "@adl/App/Components/Product/MiniProductCarouselProduct";
import XngCarousel from "@adl/App/Components/XngCarousel/XngCarousel";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { mergeWithCss } from "@adl/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const widgetId = "header-featured-products";

interface OwnProps {
    extendedStyles?: FeaturedProductsStyles;
    featuredProductsIds: string[];
    widgetId?: string;
}

const mapStateToProps = (state: CustomAppState) => ({
    products: getProductsForProductInfoList(state, widgetId),
    isMobileScreen: state.xCommon.isMobileScreen,
});

const mapDispatchToProps = {
    loadProductInfoList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FeaturedProductsStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    miniProductCarouselProductStyles?: MiniProductCarouselProductStyles;
}

const featuredProductsStyles: FeaturedProductsStyles = {
    container: {
        css: css`
            display: flex;
            & > div {
                margin-right: 25px;
            }
        `,
    },
    title: {
        size: 18,
        weight: 600,
        css: css`
            letter-spacing: 0;
            line-height: 22px;
            margin-bottom: 40px;
        `,
    },
};

const FeaturedProducts: FC<Props> = ({
    widgetId: outerWidgetId,
    featuredProductsIds,
    products,
    loadProductInfoList,
    extendedStyles,
    isMobileScreen,
}) => {
    const [styles] = useState(() => mergeWithCss(featuredProductsStyles, extendedStyles));
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch({
            type: "Components/ProductInfoLists/CompleteLoadProductInfoList",
            id: outerWidgetId || widgetId,
            productInfos: [],
            errorMessage: "",
        });
    }, []);
    useEffect(() => {
        if (featuredProductsIds.length > 0) {
            loadProductInfoList({
                id: outerWidgetId || widgetId,
                getProductCollectionParameter: { productIds: featuredProductsIds } as any,
            });
        }
    }, [featuredProductsIds]);

    if (!products || products.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <XngCarousel key={products[0].id} navigation={true} slidesPerView={3} spaceBetween={20}>
                {products?.map(product => (
                    <MiniProductCarouselProduct key={product.id} product={product} carouselId={widgetId} />
                ))}
            </XngCarousel>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(FeaturedProducts);
